import { Component, DestroyRef, inject, effect, OnInit ,Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { CanComponentDeactivate } from 'src/app/guards/can-deactivate.guard';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import * as _ from 'lodash';
import * as moment from 'moment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SettingsService } from 'src/app/shared/services/settings';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import { ActionService } from 'src/app/shared/services/action/action.service';
import { DynamicActionFormRendererComponent } from 'src/app/shared/components/dynamic-action-form-renderer/dynamic-action-form-renderer.component';
import { LtmsLoaderComponent } from 'src/app/shared/components/ltms-loader/ltms-loader.component';
import { ruleSetToExclude, categorySchema, subCategorySchema, categoryRuleSchema, subCategoryDefaultValues, ruleDefaultValues, categoryDefaultValues } from '../../constants/schemas';
interface Category {
  uuid: string;
  name: string;
  level: string;
  parent_id?: string;
  children?: Subcategory[];
  rules?: Rule[];
  is_deleted?: boolean;
  access_key_rules?: any[];
}

interface Subcategory {
  uuid: string;
  name: string;
  level: string;
  parent_id: string;
  is_deleted?: boolean;
}

interface Rule {
  id: string;
  name: string;
  description: string;
  active: boolean;
  is_enabled: boolean;
  created_date: Date;
  last_updated_on: Date;
  updated_by?: string;
  formatted_created_date?: string;
  formatted_last_updated?: string;
  category_list?: string[];
  conditions?: any;
  type?: string;
  is_deleted?: boolean;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  start: number;
  end: number;
  total: number;
  pageNo: number;
  pageFrom: number;
  pageTo: number;
  limit: number;
  disableNext: boolean;
  disablePrev: boolean;
  isNextCtrl: boolean;
}

interface CategoryTableData extends Category {
  type?: string;
  isHidden?: boolean;
  isEditMode?: boolean;
}

@Component({
  selector: 'app-litmus-action-categories',
  templateUrl: './litmus-action-categories.component.html',
  styleUrls: ['./litmus-action-categories.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, DynamicActionFormRendererComponent, LtmsLoaderComponent]
})
export class LitmusActionCategoriesComponent implements OnInit, CanComponentDeactivate {
  @Input() readonly :boolean = true
  destroyRef = inject(DestroyRef);
  // Settings and view mode
  settings = { selected: 'basic' };
  viewMode: 'basic' | 'advanced' = 'basic';
  editMode: boolean = false

  // Loading and state management
  pagerBusy = true;
  isSaving = false;
  isCancelling = false;
  isSaveEnabled = false;

  isModalOpen = false;
  modalMode: 'category' | 'subCategory' | 'rule' = 'category';

  // Brand and configuration
  brandId: string = '';
  brandDetails: any = {};
  configuration: any = {};
  hierarchyMapping: any = {};
  configurationLoaded: boolean = false;
  tableRows: any = [];

  // Data storage
  selectedCategoryId: any = '';
  selectedSubCategoryDetails: any = {};
  selectedCategoryDetails: any = {};
  selectedRuleDetails: any = {};
  categoryList: Category[] = [];
  categoryListBackup: Category[] = [];
  categoryListTableData: CategoryTableData[] = [];
  categoryRules: Rule[] = [];
  categoryRulesBackup: Rule[] = [];
  filteredCategoryRules: Rule[] = [];
  modalSchema: any = {}
  defaultValues: any = {}

  // Workflow rules fields and operators from API
  workflowFields: any[] = [];
  workflowOperators: any[] = [];

  ruleSetIdsToExclude: any[] = ['lw_event_fields_logic_ruleset', 'response_fields_logic_mandatory_ruleset', 'lw_manager_fields_logic_ruleset'];

  categoryModalSchema = _.cloneDeep(categorySchema);
  ruleModalSchema = _.cloneDeep(categoryRuleSchema);

  subCategoryModalSchema = {
    title: 'Add Sub-category',
    type: 'object',
    properties: {
      name: {
        label: 'Name',
        type: 'string',
        required: true,
        minLength: 3
      }
    }
  };



  subCategoryModalDefaultValues = {
    name: ''
  };

  ruleModalDefaultValues = {
    name: '',
    description: ''
  };

  categoryModalDefaultValues = {
    name: '',
    levels: {}
  };

  // Search functionality
  searchText: string = '';
  textToSearch: string = '';
  lwActionCategoryBasicSearch: string = '';

  // Pagination for rules
  pagination: Pagination = {
    currentPage: 1,
    totalPages: 1,
    start: 1,
    end: 10,
    total: 0,
    pageNo: 0,
    pageFrom: 1,
    pageTo: 10,
    limit: 10,
    disableNext: true,
    disablePrev: true,
    isNextCtrl: true
  };
  pagingButtons = {
    isNext: false,
    isPrevious: false,
    isLast: false,
    isFirst: false
  };

  // Column options for tables
  categoryListColumnOptions: any = null;
  ruleListColumnOptions: any = null;

  // Callee identifier
  callee = 'lwCategorySettings';

  // Modal and form data
  modalOptions: any = null;
  sortDetailForAdvanced: any = null;
  fieldDefsMap: any = {}
  constructor(
    private toastService: ToastService,
    private _settingsService: SettingsService,
    private stateService: AppStateService,
    private actionService: ActionService,
    private accountsService: AccountsService
  ) {
    effect(() => {
      if (this.stateService.initialized()) {
        this.fieldDefsMap = {}
        const brandDetail: any = this.stateService.brandDetails();
        if (!_.isEmpty(brandDetail)) {
          if (this.brandId && this.brandId === brandDetail._id && this.configurationLoaded) {
            this.brandDetails = brandDetail;
            return;
          }
          this.brandDetails = brandDetail;
          this.brandId = brandDetail._id;
          this.configurationLoaded = true;
          if (brandDetail.field_definitions && !_.isEmpty(brandDetail.field_definitions)) {
            brandDetail.field_definitions.forEach((field: any) => {
              this.fieldDefsMap[field.field_id] = field;
            })
          }
          this.onChange();
        }
      }
    })
  }

  ngOnInit() {
    this.resetData();
    this.onChange();
  }

  get isConfigurationReady(): boolean {
    return this.configurationLoaded && !this.pagerBusy;
  }


  /**
   * Reset all data to initial state
   */
  private resetData() {
    // Reset pagination variables
    this.pagination.pageNo = 0;
    this.pagination.pageFrom = 1;
    this.pagination.total = 0;
    this.pagination.limit = 10;
    this.pagination.disableNext = true;
    this.pagination.disablePrev = true;
    this.pagination.isNextCtrl = true;
    this.pagingButtons = {
      isNext: false,
      isPrevious: false,
      isLast: false,
      isFirst: false
    };

    // Reset search
    this.textToSearch = '';
    this.lwActionCategoryBasicSearch = '';

    // Reset settings
    this.settings = { selected: 'basic' };
    this.callee = 'lwCategorySettings';

    // Reset data arrays
    this.categoryList = [];
    this.categoryListBackup = [];
    this.categoryListTableData = [];
    this.categoryRules = [];
    this.categoryRulesBackup = [];
    this.filteredCategoryRules = [];

    // Reset column options
    this.categoryListColumnOptions = null;
    this.ruleListColumnOptions = null;
  }

  onFormSubmit(value: any) {
    if (this.modalMode === 'rule') {
      this.prepareReqParamsforRule(value);
    } else {
      this.prepareReqParamsforCategory(value);
    }
  }

  prepareReqParamsforCategory(formData: any) {
    // Build access_key_rules from organisationLevels (matching old dashboard logic)
    const access_key_rules: any[] = [];
    const visibilityData = formData.organisationLevels || formData.set_level_based_visibility;

    if (visibilityData && typeof visibilityData === 'object') {
      const levels: string[] = [];
      for (const [key, value] of Object.entries(visibilityData)) {
        if (value === true) {
          levels.push(key);
        }
      }
      if (levels.length > 0) {
        access_key_rules.push({ levels: levels, type: 'visibility' });
      } else {
        access_key_rules.push({ levels: [], type: 'visibility' });
      }
    } else {
      access_key_rules.push({ levels: [], type: 'visibility' });
    }

    // Determine level and parent_id
    let level = '0';
    let parent_id: string | null = null;

    if (this.modalMode === 'subCategory' && this.selectedCategoryId !== '') {
      level = '1';
      parent_id = this.selectedCategoryId;
    }

    // Check if editing existing category/subcategory
    if (!_.isEmpty(this.selectedCategoryDetails)) {
      // Editing a category (level 0)
      level = '0';
      parent_id = null;
      const categoryDetail: any = {
        uuid: this.selectedCategoryDetails.uuid,
        level: level,
        name: formData.name,
        parent_id: parent_id,
        access_key_rules: access_key_rules
      };
      this.updateCategory(categoryDetail);
    } else if (!_.isEmpty(this.selectedSubCategoryDetails)) {
      // Editing a subcategory (level 1)
      level = '1';
      parent_id = this.selectedSubCategoryDetails.parent_id || null;
      const categoryDetail: any = {
        uuid: this.selectedSubCategoryDetails.uuid,
        level: level,
        name: formData.name,
        parent_id: parent_id,
        access_key_rules: access_key_rules
      };
      this.updateCategory(categoryDetail);
    } else {
      // Creating new category/subcategory
      const categoryDetail: any = {
        name: formData.name,
        level: level,
        parent_id: parent_id,
        access_key_rules: access_key_rules
      };
      this.saveConfiguration(categoryDetail);
    }
  }

  prepareReqParamsforRule(formData: any) {
    // Extract category_list from multi-select format (array of category objects or UUIDs)
    const categoryList: string[] = [];
    if (formData.category_list && Array.isArray(formData.category_list)) {
      // Multi-select returns array of selected items
      formData.category_list.forEach((item: any) => {
        if (typeof item === 'string') {
          // If it's just a UUID string
          categoryList.push(item);
        } else if (item && item.uuid) {
          // If it's an object with uuid property
          categoryList.push(item.uuid);
        } else if (item && item.value) {
          // If it's an object with value property
          categoryList.push(item.value);
        }
      });
    } else if (formData.category_list && typeof formData.category_list === 'object' && !Array.isArray(formData.category_list)) {
      // Fallback: handle old checkbox-table format if needed
      for (const [key, value] of Object.entries(formData.category_list)) {
        if (value === true) {
          categoryList.push(key);
        }
      }
    }

    // Extract conditions from group-box format
    // Group-box stores data as an object with keys based on field labels (converted to keys)
    let conditions: any[] = [];
    if (formData.conditions && typeof formData.conditions === 'object') {
      // Check if it's already an array (from edit mode)
      if (Array.isArray(formData.conditions)) {
        conditions = formData.conditions;
      } else {
        // Extract from group-box object format (keys are converted from labels)
        const conditionObj = formData.conditions;
        // Map group-box form values to condition structure
        // Keys are converted using toKey() which converts "Field" -> "field", "Value Type" -> "valueType", etc.
        const fieldId = conditionObj.field || conditionObj.Field || '';
        const operatorId = conditionObj.operator || conditionObj.Operator || '';
        const value = conditionObj.value !== undefined ? conditionObj.value : (conditionObj.Value !== undefined ? conditionObj.Value : '');
        const valueType = conditionObj.valueType || conditionObj['Value Type'] || 'string';
        const joinId = conditionObj.joinWithNext || conditionObj['Join With Next'] || '$and';

        // Only add if all required fields are present
        if (fieldId && operatorId && value !== undefined && value !== '') {
          conditions.push({
            field_id: fieldId,
            operator_id: operatorId,
            value: value,
            value_type: valueType,
            join_id: joinId
          });
        }
      }
    }

    const ruleData: Rule = {
      id: this.selectedRuleDetails.id || '',
      name: formData.name,
      description: formData.description || '',
      active: false,
      is_enabled: this.selectedRuleDetails.is_enabled !== undefined ? this.selectedRuleDetails.is_enabled : false,
      created_date: this.selectedRuleDetails.created_date || new Date(),
      last_updated_on: new Date(),
      category_list: categoryList,
      conditions: conditions
    };

    // Check for duplicate rule name
    const ruleWithSameName = this.categoryRulesBackup.filter((rule: Rule) => {
      return (rule.id !== ruleData.id && rule.name.toLowerCase().trim() === ruleData.name.toLowerCase().trim());
    });

    if (ruleWithSameName && ruleWithSameName.length) {
      this.toastService.showPopup('WARNING', 'Warning', 'Rule name already exists, please enter another name', 3000);
      return;
    }

    if (this.selectedRuleDetails.id) {
      // Update existing rule
      this.showConfirmationBox('Confirmation', 'Please note that this change will Override the previous configuration. Are you sure, you want to proceed?', (answer: boolean) => {
        if (answer) {
          this.updateRule(ruleData);
        }
      });
    } else {
      // Create new rule
      this.updateRule(ruleData);
    }
  }

  /**
   * Main change handler - called when component initializes or data changes
   */
  private onChange() {
    this.pagerBusy = true;
    this.resetData();
    if (!this.brandId || _.isEmpty(this.brandDetails)) {
      return;
    }

    this.prepareHierarchyMapping();

    // Get table configuration for category list table
    this.categoryListColumnOptions = this.getColumnOptionsBySelectedSettingType('basic');

    // Get and prepare category list data
    this.getCategoryList();

    // Get table configuration for rule list table
    this.ruleListColumnOptions = this.getColumnOptionsBySelectedSettingType('advanced');
  }

  prepareHierarchyMapping() {

    const hierarchyList = this.actionService.prepareViewTypeDropdown(this.brandDetails)
    let unformattedMapping = hierarchyList?.hierarchyMapping

    // Get all keys from the object
    const keys = Object.keys(unformattedMapping);

    // Get the last key
    const lastKey = keys[keys.length - 1];

    // Check if the value is undefined and update it
    if (unformattedMapping[lastKey] === undefined) {
      unformattedMapping[lastKey] = "Touchpoint";
    }

    this.hierarchyMapping = unformattedMapping;
    this.prepareTableRowForCategoryModal()
  }

  prepareTableRowForCategoryModal() {
    let tableRows: any[] = [];
    const keys = Object.keys(this.hierarchyMapping);

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const label = this.hierarchyMapping[key]!;

      tableRows.push({
        label,
        key,
        default: true // mark first one as default
      });
    }
    this.tableRows = tableRows
    this.categoryModalDefaultValues.levels = tableRows.map((row: any) => {
      if (row.default) {
        return {
          label: row.label,
          key: row.key,
          visible: true
        }
      }
      return {
        label: row.label,
        key: row.key,
        visible: false
      }
    });
  }

  setViewMode(mode: 'basic' | 'advanced') {
    this.viewMode = mode;
    this.settings.selected = mode;
    this.getDivBySettingType(mode);
  }

  /**
   * Get column options based on selected setting type
   */
  private getColumnOptionsBySelectedSettingType(type: string): any {
    if (type === 'basic') {
      return {
        sortable: false,
        id: this.callee + '_basic',
        columns: [
          {
            field_name: 'name',
            display_name: 'Category/Sub-Category Name',
            type: 'html',
            header_cell_width: '90%',
            body_cell_width: '90%',
            header_padding_left: '10px',
          },
          {
            field_name: 'editBtn',
            display_name: '',
            type: 'html',
            header_cell_width: '5%',
            body_cell_width: '5%',
          },
          {
            field_name: 'deleteBtn',
            display_name: '',
            type: 'html',
            header_cell_width: '5%',
            body_cell_width: '5%',
          }
        ]
      };
    } else if (type === 'advanced') {
      return {
        id: this.callee + '_advanced',
        header: true,
        sortable: false,
        sortDateByAsc: true,
        columns: [
          {
            field_name: 'name',
            display_name: 'Rules',
            header_cell_width: '30%',
            body_cell_width: '29%',
            type: 'text',
            border_bottom: true,
            is_sort_in_asc: true,
            sort_by: 'text',
            order: '2',
            header_padding_left: '1%'
          },
          {
            field_name: 'active',
            display_name: 'Active',
            header_cell_width: '10%',
            body_cell_width: '9%',
            type: 'html',
            border_bottom: true,
            cell_margin_bottom: '12px',
            order: '3',
            header_padding_left: '1%'
          },
          {
            field_name: 'formatted_created_date',
            field_to_sort: 'created_date',
            display_name: 'Created Date',
            header_cell_width: '15%',
            body_cell_width: '15%',
            type: 'text',
            is_sort_in_asc: true,
            border_bottom: true,
            sort_by: 'date',
            order: '4',
            header_padding_left: '1%'
          },
          {
            field_name: 'formatted_last_updated',
            field_to_sort: 'last_updated_on',
            display_name: 'Last Updated',
            header_cell_width: '33%',
            body_cell_width: '32%',
            type: 'text',
            is_sort_in_asc: true,
            border_bottom: true,
            sort_by: 'date',
            order: '5',
            header_padding_left: '1%'
          },
          {
            field_name: 'editBtn',
            display_name: '',
            type: 'html',
            header_cell_width: '5%',
            body_cell_width: '5%',
            border_bottom: true,
            order: '6'
          },
          {
            field_name: 'deleteBtn',
            display_name: '',
            type: 'html',
            header_cell_width: '5%',
            body_cell_width: '5%',
            border_bottom: true,
            order: '7'
          }
        ]
      };
    }
    return null;
  }

  /**
   * Get category list from API using SettingsService
   */
  private getCategoryList() {
    this._settingsService.getCategoryList(this.brandId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response && response.data && response.data.category_list && response.data.category_list.length) {
            this.categoryList = response.data.category_list;
            const managers = (response.data.managers && response.data.managers.length) ? response.data.managers : [];
            this.categoryListBackup = _.cloneDeep(response.data.category_list);
            this.categoryListTableData = this.flattenCategory(response.data.category_list);

            this.categoryListBackup = _.filter(_.cloneDeep(response.data.category_list), (category: Category) => {
              return !category.is_deleted;
            });

            this.getAdvancedData(this.categoryListBackup, managers);
            this.pagerBusy = false;
          } else {
            this.categoryListTableData = [];
            this.categoryList = [];
            this.categoryListBackup = [];
            this.pagerBusy = false;
          }
        },
        error: (error: any) => {
          console.error('Error fetching category list:', error);
          this.categoryListTableData = [];
          this.categoryList = [];
          this.categoryListBackup = [];
          this.pagerBusy = false;
          this.showAlert('Error', 'Failed to load categories', 'error');
        }
      });
  }


  openCategoryModal(): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    // Check view mode to determine what to open
    if (this.viewMode === 'advanced') {
      this.openRuleModal();
    } else {
      // Basic mode - open category modal
      this.modalMode = 'category';
      this.editMode = false;
      // Reset default values for new category
      this.categoryModalDefaultValues = {
        name: '',
        levels: []
      };
      // Reset modal title and tableRows for new category
      const modalSchemaCopy = _.cloneDeep(this.categoryModalSchema);
      modalSchemaCopy.title = 'Add Category';
      // Reset tableRows to original defaults (all true for new category)
      this.categoryModalDefaultValues.levels = this.tableRows.map((row: any) => ({
        ...row,
        visible: true // Reset to original default for new category
      }));
      this.modalSchema = modalSchemaCopy;
      this.defaultValues = this.categoryModalDefaultValues;
      this.isModalOpen = true;


    }
  }

  openRuleModal(): void {
    this.modalMode = 'rule';
    this.editMode = false;

    // Prepare category list for multi-select
    const categoryOptions = this.categoryListBackup.map((category: Category) => ({
      name: category.name,
      uuid: category.uuid,
      label: category.name, // For backward compatibility
      value: category.uuid // For backward compatibility
    }));
    this.getWorkflowRules().then(() => {
      this.initializeRuleModal(categoryOptions);
    })

  }

  /**
   * Edit rule
   */
  editRule(rule: Rule) {
    const clonedRule = _.cloneDeep(rule);
    this.selectedRuleDetails = clonedRule;
    this.modalMode = 'rule';
    this.editMode = true;

    // Prepare category list for multi-select
    const categoryOptions = this.categoryListBackup.map((category: Category) => ({
      name: category.name,
      uuid: category.uuid,
      label: category.name, // For backward compatibility
      value: category.uuid // For backward compatibility
    }));

    // Map category_list to multi-select format (array of selected category objects)
    const selectedCategories: any[] = [];
    if (clonedRule.category_list && Array.isArray(clonedRule.category_list)) {
      clonedRule.category_list.forEach((categoryId: string) => {
        const category = this.categoryListBackup.find((cat: Category) => cat.uuid === categoryId);
        if (category) {
          selectedCategories.push({
            name: category.name,
            uuid: category.uuid,
            label: category.name,
            value: category.uuid
          });
        }
      });
    }

    // Map conditions to group-box format
    // Group-box expects an object with keys matching the field labels (converted to keys)
    // For now, we'll use the first condition if multiple exist, or create empty structure
    let conditionsList: any[] = [];
    if (clonedRule.conditions && Array.isArray(clonedRule.conditions) && clonedRule.conditions.length > 0) {
      conditionsList = clonedRule.conditions;
    }
    // Empty condition structure for new/edit
    conditionsList.push({
      "disabled": false,
      "ruleset_id": "",
      "field_id": "",
      "field_id_value": "",
      "operator_id": "",
      "value": "",
      "value_type": "",
      "join_id": "",
      "editing": true,
      "unique_id": ""
    });

    this.getWorkflowRules().then(() => {
      this.initializeEditRuleModal(categoryOptions, selectedCategories, conditionsList);
    })
  }


  /**
   * Initialize rule modal with category options and workflow fields/operators
   */
  private initializeRuleModal(categoryOptions: any[]): void {
    // Reset default values for new rule
    const ruleDefaultValues = {
      name: '',
      description: '',
      category_list: [], // Multi-select uses array format
      conditions: [{
        "disabled": false,
        "ruleset_id": "",
        "field_id": "",
        "field_id_value": "",
        "operator_id": "",
        "value": "",
        "value_type": "",
        "join_id": "",
        "editing": true,
        "unique_id": ""
      }]
    };

    // Reset modal schema for new rule
    const modalSchemaCopy = _.cloneDeep(this.ruleModalSchema);
    modalSchemaCopy.title = 'Add Rule';
    // Update category_list options with current categories
    modalSchemaCopy.basic.properties.category_list.options = categoryOptions;




    // Update conditions field options with API data
    if (!_.hasIn(modalSchemaCopy, 'conditions.properties')) {
      modalSchemaCopy.conditions.properties = {}
    }
    modalSchemaCopy.conditions.properties.rulesetlist = [...this.workflowFields]
    modalSchemaCopy.conditions.properties.rulesetHash = { ...this.rulesetHash }
    modalSchemaCopy.conditions.properties.fieldDefsMap = { ...this.fieldDefsMap }

    this.modalSchema = modalSchemaCopy;
    this.defaultValues = ruleDefaultValues;
    this.isModalOpen = true;
  }


  /**
   * Initialize edit rule modal with category options and workflow fields/operators
   */
  private initializeEditRuleModal(categoryOptions: any[], selectedCategories: any[], conditionsObj: any): void {
    // Create a new object with the rule data for default values
    const ruleDefaultValues = {
      name: this.selectedRuleDetails.name || '',
      description: this.selectedRuleDetails.description || '',
      category_list: selectedCategories, // Multi-select uses array format
      conditions: conditionsObj,
      id: this.selectedRuleDetails.id || ''
    };

    // Update modal schema for edit mode
    const modalSchemaCopy = _.cloneDeep(this.ruleModalSchema);
    modalSchemaCopy.title = 'Edit Rule';
    // Update category_list options with current categories
    modalSchemaCopy.basic.properties.category_list.options = categoryOptions;

    // Update conditions field options with API data
    if (!_.hasIn(modalSchemaCopy, 'conditions.properties')) {
      modalSchemaCopy.conditions.properties = {}
    }
    modalSchemaCopy.conditions.properties.rulesetlist = [...this.workflowFields];
    modalSchemaCopy.conditions.properties.rulesetHash = { ...this.rulesetHash };
    modalSchemaCopy.conditions.properties.fieldDefsMap = { ...this.fieldDefsMap };

    this.modalSchema = modalSchemaCopy;
    this.defaultValues = ruleDefaultValues;
    this.isModalOpen = true;
  }

  /**
   * Delete rule
   */
  deleteRule(rule: Rule) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    this.showConfirmationBox('Confirmation', 'Are you sure you want to delete this rule?', (answer: boolean) => {
      if (answer) {
        rule.is_deleted = true;
        this.updateRule(rule);
      }
    });
  }
  rulesetHash: any = {}
  getWorkflowRules() {
    return new Promise((resolve) => {
      // Fetch workflow rules (fields and operators) from API
      this._settingsService.getWorkflowRules(this.brandId, 3)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {

            // Extract fields and operators from all rulesets
            this.workflowFields = [];
            this.workflowOperators = [];

            // Handle different possible response structures
            let rulesets: any[] = [];
            if (response && response.data) {
              if (Array.isArray(response.data)) {
                rulesets = response.data;
              } else if (response.data.data && Array.isArray(response.data.data)) {
                rulesets = response.data.data;
              } else if (response.data.rulesets && Array.isArray(response.data.rulesets)) {
                rulesets = response.data.rulesets;
              }
            }

            if (this.ruleSetIdsToExclude && this.ruleSetIdsToExclude.length) {
              rulesets = _.filter(rulesets, (ruleSet: any) => {
                if (!(ruleSet._id && this.ruleSetIdsToExclude.indexOf(ruleSet._id) >= 0)) {
                  return ruleSet;
                }
              })
            }


            rulesets = _.map(rulesets, (condition: any) => {
              if (condition._id == "response_fields_logic_ruleset") {
                condition = this.getExternalFields(condition);
              }
              // add property id and text as this will be used in select2
              condition["id"] = condition["_id"];
              condition["text"] = condition["name"];

              // update workflowConditionsVM.rulesetHash
              this.rulesetHash[condition["_id"]] = condition;
              return condition;
            });

            _.each(rulesets, (rule: any) => {
              var options: any = _.get(rule, "schema.options");
              var operators: any = _.get(rule, "schema.operators");
              const rulesetId = rule["_id"];
              if (!_.isEmpty(options)) {
                _.each(options, (fieldDetail: any, key: string) => {
                  const valueType = _.get(fieldDetail, 'value_type')
                  if ((!_.hasIn(fieldDetail.operators) || _.isEmpty(fieldDetail.operators)) && !_.isEmpty(operators) && _.has(operators, valueType) && !_.isEmpty(operators[valueType])) {
                    fieldDetail.operators = _.cloneDeep(operators[valueType])
                  }
                  // fieldDetail['id'] = fieldDetail['field_id'];
                  fieldDetail['id'] = fieldDetail['unique_id'];
                  fieldDetail['text'] = fieldDetail['display_name'];
                  fieldDetail['ruleset_id'] = rulesetId;
                  fieldDetail['ruleset_key'] = key;
                  this.workflowFields.push(fieldDetail);
                })
              }

            });

            // // Process each ruleset to extract fields and operators
            // rulesets.forEach((ruleset: any, index: number) => {

            //   if (ruleset._id && !this.ruleSetIdsToExclude.includes(ruleset._id)) {


            //     // Extract fields from schema.options
            //     if (ruleset && ruleset.schema) {
            //       let operators = []
            //       if (ruleset?.schema?.operators && !_.isEmpty(ruleset.schema.operators)) {
            //         operators = ruleset.schema.operators
            //       }

            //       // schema.options is an OBJECT (key-value pairs), not an array!
            //       if (ruleset.schema.options && typeof ruleset.schema.options === 'object') {
            //         const optionsObj = ruleset.schema.options;
            //         const isArray = Array.isArray(optionsObj);
            //         const optionsCount = isArray ? optionsObj.length : Object.keys(optionsObj).length;


            //         // Handle both array and object structures
            //         const optionsToProcess = isArray ? optionsObj : Object.values(optionsObj);

            //         // Process EVERY option
            //         optionsToProcess.forEach((field: any, fieldIndex: number) => {
            //           if (!field || typeof field !== 'object') {
            //             console.warn(`  Skipping invalid field at index ${fieldIndex}:`, field);
            //             return;
            //           }

            //           // Extract field properties - following AngularJS pattern
            //           // Use unique_id as primary identifier, fallback to field_id
            //           const fieldValue = field.unique_id || field.field_id || field.id || field._id;
            //           const fieldLabel = field.display_name || field.name || field.label || fieldValue;

            //           if (fieldValue) {
            //             const fieldObj = {
            //               label: fieldLabel || 'Unnamed Field',
            //               value: fieldValue,
            //               // Store additional properties for later use
            //               field_id: field.field_id,
            //               unique_id: field.unique_id,
            //               value_type: field.value_type,
            //               originalField: field,
            //               operators: operators
            //             };

            //             // Check if field already exists (by unique_id or field_id to avoid duplicates)
            //             const exists = this.workflowFields.some((f: any) =>
            //               f.value === fieldObj.value ||
            //               (fieldObj.unique_id && f.unique_id === fieldObj.unique_id) ||
            //               (fieldObj.field_id && f.field_id === fieldObj.field_id)
            //             );

            //             if (!exists) {
            //               this.workflowFields.push(fieldObj);
            //             } else {
            //             }
            //           } else {
            //             console.warn(`  ⊗ Field missing identifier at index ${fieldIndex}:`, field);
            //           }
            //         });

            //       } else {
            //         console.warn(`Ruleset ${index} - schema.options is not an object or doesn't exist`);
            //         console.warn(`  schema.options type:`, typeof ruleset.schema.options);
            //         console.warn(`  schema.options value:`, ruleset.schema.options);
            //       }

            //       // Extract ALL operators from schema.operators
            //       // Operators can be: 1) Array, 2) Object with type keys (string, number, etc.), 3) Flat object
            //       if (ruleset.schema.operators) {
            //         let operatorsToProcess: any[] = [];

            //         if (Array.isArray(ruleset.schema.operators)) {
            //           // If it's an array, use it directly
            //           operatorsToProcess = ruleset.schema.operators;
            //         } else if (typeof ruleset.schema.operators === 'object') {
            //           // If it's an object, it might be nested by type (string, number, date, etc.)
            //           // Flatten all operator arrays from all types
            //           const operatorValues = Object.values(ruleset.schema.operators);
            //           operatorsToProcess = operatorValues.flatMap((op: any) => {
            //             if (Array.isArray(op)) {
            //               return op;
            //             } else if (op && typeof op === 'object') {
            //               return [op];
            //             }
            //             return [];
            //           });
            //         }

            //         // Process each operator
            //         operatorsToProcess.forEach((operator: any, opIndex: number) => {
            //           let opObj: any = null;

            //           if (typeof operator === 'object' && operator !== null) {
            //             // Operator object - extract _id as value, label/name/display_name as label
            //             const opValue = operator._id || operator.id || operator.value || operator.operator_id;
            //             const opLabel = operator.label || operator.name || operator.display_name || operator.text || opValue;

            //             if (opValue) {
            //               opObj = {
            //                 label: opLabel,
            //                 value: opValue,
            //                 originalOperator: operator
            //               };
            //             }
            //           } else if (typeof operator === 'string') {
            //             // Operator as string - map to readable label
            //             const operatorMap: any = {
            //               '$eq': 'Equals',
            //               '$ne': 'Not Equals',
            //               '$lt': 'Less Than',
            //               '$lte': 'Less Than or Equal',
            //               '$gt': 'Greater Than',
            //               '$gte': 'Greater Than or Equal',
            //               '$in': 'Contains',
            //               '$has': 'Contains'
            //             };
            //             opObj = {
            //               label: operatorMap[operator] || operator,
            //               value: operator
            //             };
            //           }

            //           if (opObj && opObj.value) {
            //             // Check if operator already exists (by value to avoid duplicates)
            //             const exists = this.workflowOperators.some((op: any) => op.value === opObj.value);
            //             if (!exists) {
            //               this.workflowOperators.push(opObj);
            //             } else {
            //             }
            //           } else {
            //             console.warn(`  ⊗ Could not process operator at index ${opIndex}:`, operator);
            //           }
            //         });

            //       } else {
            //         console.warn(`Ruleset ${index} - No operators found in schema`);
            //       }
            //     } else {
            //       console.warn(`Ruleset ${index} - No schema found in ruleset`);
            //       console.warn(`  Ruleset keys:`, ruleset ? Object.keys(ruleset) : 'ruleset is null/undefined');
            //     }
            //   }
            // });


            // // If no fields found, use fallback
            // if (this.workflowFields.length === 0) {
            //   console.warn('No fields found in API response, using fallback');
            //   this.workflowFields = [
            //     { label: 'Status ID List', value: 'status_id_list' },
            //     { label: 'Category', value: 'category' },
            //     { label: 'Primary Rating', value: 'feed_params.primary_rating' },
            //     { label: 'Tags', value: 'tags' }
            //   ];
            // }

            // // If no operators found, use default operators
            if (this.workflowOperators.length === 0) {
              console.warn('No operators found in API response, using fallback');
              this.workflowOperators = [
                { label: 'Equals', value: '$eq' },
                { label: 'Not Equals', value: '$ne' },
                { label: 'Less Than', value: '$lt' },
                { label: 'Less Than or Equal', value: '$lte' },
                { label: 'Greater Than', value: '$gt' },
                { label: 'Greater Than or Equal', value: '$gte' },
                { label: 'Contains', value: '$in' }
              ];
            }


            resolve({
              workflowFields: this.workflowFields,
              workflowOperators: this.workflowOperators
            })
          },
          error: (error: any) => {
            console.error('Error fetching workflow rules:', error);
            // Fallback to default values if API fails
            this.workflowFields = [
              { label: 'Status ID List', value: 'status_id_list' },
              { label: 'Category', value: 'category' },
              { label: 'Primary Rating', value: 'feed_params.primary_rating' },
              { label: 'Tags', value: 'tags' }
            ];
            this.workflowOperators = [
              { label: 'Equals', value: '$eq' },
              { label: 'Not Equals', value: '$ne' },
              { label: 'Less Than', value: '$lt' },
              { label: 'Less Than or Equal', value: '$lte' },
              { label: 'Greater Than', value: '$gt' },
              { label: 'Greater Than or Equal', value: '$gte' },
              { label: 'Contains', value: '$in' }
            ];
            resolve({
              workflowFields: this.workflowFields,
              workflowOperators: this.workflowOperators
            })
          }
        });
    })

  }

  getExternalFields(ruleSet) {
    var addedItemsMap = {}
    if (ruleSet && ruleSet.schema && !(_.isEmpty(ruleSet.schema.options))) {
      for (var field_ in this.fieldDefsMap) {
        var fieldItem = this.fieldDefsMap[field_]
        if (fieldItem.custom_parameter_type == 4) {
          if (fieldItem.group_id && !(addedItemsMap[fieldItem.group_id])) {
            var groupField = this.fieldDefsMap[fieldItem.group_id];
            if (groupField && !groupField.group_id) {
              addedItemsMap[fieldItem.group_id] = 1;
              this.setRuleSetObj(this.fieldDefsMap[fieldItem.group_id], ruleSet)
            }
          }
          if (fieldItem.data_type == "date") {
            this.setRuleSetObj(this.fieldDefsMap[fieldItem.field_id], ruleSet, (fieldItem["display_name"] || fieldItem["name"]))
          }
        }

      }
    }
    return ruleSet;
  }

  /* By Bhunesh - This function prepares dynamic ruleset object for all the custom accordions and fields(date fields)*
                  to be moved to backend
          */
  setRuleSetObj(fieldItem: any, ruleSet: any, textToPrepend?: any) {
    if (ruleSet && fieldItem) {
      var group = this.fieldDefsMap[fieldItem.group_id];
      ["Business", ""].forEach(function (ageType) {
        ["Hours", "Minutes"].forEach(function (timeType) {
          var field_id: any = null;
          var name: any = null;
          var sep: any = (ageType == "Business") ? " " : "";
          var ruleSetObjectPath: any;
          if (textToPrepend) {
            var groupname = "";
            if (group && (group.name || group.display_name)) {
              groupname = group.display_name || group.name;
            }
            field_id = "response__has_" + fieldItem.field_id + "_diff_" + ageType.toLowerCase() + "_" + timeType.toLowerCase();
            name = groupname + " - " + textToPrepend + ' (' + ageType.trim() + sep + timeType + ')';
            ruleSetObjectPath = "response.litmus_processed_data." + fieldItem["field_id"];
          } else {
            field_id = "response__has_" + fieldItem.field_id + "_last_updated_" + ageType.toLowerCase() + "_" + timeType.toLowerCase();
            name = "Time Since Last " + (fieldItem["display_name"] || fieldItem["name"]) + " Update (" + ageType.trim() + sep + timeType + ")";
            ruleSetObjectPath = "response.update_logs." + fieldItem["field_id"] + ".last_updated_on";
          }
          ruleSet.schema.options[field_id] = {
            "name": name,
            "should_evaluate": false,
            "field_id": ruleSetObjectPath,
            "display_name": name,
            "value_type": "date",
            "unique_id": "response_fields_logic_ruleset___" + field_id
          }
        })
      })
    }
  }


  openSubCategoryModal(categoryId) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    this.modalMode = 'subCategory';
    this.editMode = false;
    this.selectedCategoryId = categoryId;
    // Reset default values for new subcategory
    const subCategoryDefaultValues = {
      name: '',
      parent_id: categoryId,
      level: '1'
    };
    // Reset modal schema for new subcategory
    const modalSchemaCopy = _.cloneDeep(this.subCategoryModalSchema);
    modalSchemaCopy.title = 'Add Sub-category';
    this.modalSchema = modalSchemaCopy;
    this.defaultValues = subCategoryDefaultValues;
    this.isModalOpen = true;
  }

  closeCategoryModal(): void {
    this.isModalOpen = false;
    this.editMode = false;
    this.selectedCategoryId = '';
    this.selectedSubCategoryDetails = {};
    this.selectedCategoryDetails = {};
    this.selectedRuleDetails = {};
    this.defaultValues = {
      name: '',
      organisationLevels: {}
    };
  }

  /**
   * Flatten category structure for UI rendering
   */
  private flattenCategory(categoryList: Category[]): CategoryTableData[] {
    const list: CategoryTableData[] = [];
    _.each(categoryList, (category: Category) => {
      list.push(category as CategoryTableData);
      if (category.children && category.children.length) {
        _.each(category.children, (child: Subcategory) => {
          (child as any).parent_id = category.uuid;
        });
        list.push(...category.children as CategoryTableData[]);
      }
      list.push({ type: 'html_input', isHidden: true, parent_id: category.uuid } as CategoryTableData);
      list.push({ type: 'html_button', parent_id: category.uuid } as CategoryTableData);
    });
    return list;
  }

  /**
   * Get advanced data for rules
   */
  private getAdvancedData(categoryList: Category[], managers: any[]) {
    const categoryRulesMap: any = {
      ruleDetail: {},
      categories: {}
    };
    const categoryRules: Rule[] = [];
    const managersMapping: any = {};

    _.each(categoryList, (category: Category) => {
      if (category.rules) {
        _.each(category.rules, (rule: Rule) => {
          if (!rule.is_deleted) {
            categoryRulesMap.ruleDetail[rule.id] = rule;
            if (!categoryRulesMap.categories[rule.id]) {
              categoryRulesMap.categories[rule.id] = [];
            }
            categoryRulesMap.categories[rule.id].push(category.uuid);
          }
        });
      }
    });

    _.each(managers, (manager: any) => {
      if (!_.isEmpty(manager)) {
        managersMapping[manager._id] = manager;
      }
    });

    for (const ruleId in categoryRulesMap.categories) {
      const rulesFor = {
        category_list: categoryRulesMap.categories[ruleId]
      };
      const ruleAndCategoryDetail = Object.assign(categoryRulesMap.ruleDetail[ruleId], rulesFor);
      const clonedRule = _.cloneDeep(ruleAndCategoryDetail);

      if (!clonedRule.last_updated_on) {
        clonedRule.last_updated_on = clonedRule.created_date;
      }

      // Format dates
      clonedRule.formatted_created_date = moment(clonedRule.created_date).format('DD-MMM-YY HH:mm');
      clonedRule.formatted_last_updated = moment(clonedRule.last_updated_on).format('DD-MMM-YY HH:mm');

      if (clonedRule.last_updated_by && managersMapping[clonedRule.last_updated_by]) {
        const manager = managersMapping[clonedRule.last_updated_by];
        if (manager.name && manager.name !== '') {
          clonedRule.formatted_last_updated = clonedRule.formatted_last_updated + ' by ' + manager.name;
        } else if (manager.email_address && manager.email_address !== '') {
          clonedRule.formatted_last_updated = clonedRule.formatted_last_updated + ' by ' + manager.email_address;
        }
      }
      categoryRules.push(clonedRule);
    }

    this.categoryRulesBackup = _.cloneDeep(categoryRules);
    this.categoryRulesBackup = this.sortBySequence('created_date', this.categoryRulesBackup, { is_date: true, asc: true });
    this.pagination.total = this.categoryRulesBackup.length;
    this.getPageWiseRuleData();
    this.sortDetailForAdvanced = { sort_by: 'date', field_name: 'created_date', is_sort_in_asc: true };
  }

  /**
   * Sort data by sequence
   */
  private sortBySequence(key: string, list: any[], params: any) {
    try {
      let value: any = null;
      const sortedList = _.sortBy(list, (item: any) => {
        value = params.is_date ? new Date(item[key]).getTime() : item[key];
        return params.asc ? -value : value;
      });
      const isToSortString = (!params.is_date && params.asc);
      return isToSortString ? sortedList.reverse() : sortedList;
    } catch (e) {
      return list;
    }
  }

  /**
   * Get page-wise rule data
   */
  private getPageWiseRuleData() {
    let categoryRulesBackup = _.cloneDeep(this.categoryRulesBackup);
    let pageNo = this.pagination.pageNo;

    if (this.textToSearch && this.textToSearch !== '') {
      categoryRulesBackup = _.cloneDeep(this.filteredCategoryRules);
    }

    if (this.pagination.isNextCtrl) {
      this.pagination.pageNo = this.pagination.pageNo + 1;
      pageNo = this.pagination.pageNo;
    }

    if (!this.pagination.isNextCtrl && pageNo === 0) {
      pageNo = 1;
      this.pagination.pageNo = 1;
    }

    const getDataFrom = ((pageNo - 1) * this.pagination.limit);
    this.pagination.total = categoryRulesBackup.length;
    this.categoryRules = categoryRulesBackup.splice(getDataFrom, this.pagination.limit);
    this.pagination.totalPages = Math.max(1, Math.ceil((this.pagination.total || 0) / this.pagination.limit));

    if (this.categoryRules && this.categoryRules.length && (this.categoryRules.length === this.pagination.limit)) {
      this.pagination.pageFrom = ((pageNo - 1) * this.pagination.limit) + 1;
      this.pagination.pageTo = (pageNo) * this.pagination.limit;
      this.pagination.disableNext = (this.pagination.pageTo === this.pagination.total);
      this.pagination.disablePrev = (this.pagination.pageNo === 1);
    } else if (this.categoryRules && this.categoryRules.length && (this.categoryRules.length < this.pagination.limit)) {
      this.pagination.pageFrom = ((pageNo - 1) * this.pagination.limit) + 1;
      this.pagination.pageTo = (((pageNo) * this.pagination.limit) - (this.pagination.limit - this.categoryRules.length));
      this.pagination.disableNext = true;
      this.pagination.disablePrev = (this.pagination.pageNo === 1);
    } else {
      this.pagination.pageFrom = 0;
      this.pagination.pageTo = 0;
      this.pagination.disableNext = true;
      this.pagination.disablePrev = true;
    }

    this.updatePagingButtons();
  }

  private updatePagingButtons(): void {
    const hasData = this.pagination.total > 0;
    const pageNo = this.pagination.pageNo || 1;
    const totalPages = this.pagination.totalPages || 1;
    const isDataLessThanLimit = this.categoryRules && this.categoryRules.length && (this.categoryRules.length <= this.pagination.limit);
    this.pagingButtons = {
      isFirst: (hasData && pageNo > 1 && isDataLessThanLimit) ? true : false,
      isPrevious: (hasData && pageNo > 1 && isDataLessThanLimit) ? true : false,
      isNext: (hasData && pageNo < totalPages && isDataLessThanLimit) ? true : false,
      isLast: (hasData && pageNo < totalPages && isDataLessThanLimit) ? true : false
    };
  }

  /**
   * Handle setting type change
   */
  private getDivBySettingType(type: string) {
    if (type === 'basic') {
      this.settings.selected = 'basic';
      this.callee = 'lwCategoryBasicSettings';
      this.showBasicSettings();
      this.resetBasicSettings();
    } else if (type === 'advanced') {
      this.settings.selected = 'advanced';
      this.callee = 'lwCategoryAdvancedSettings';
      this.showAdvancedSettings();
      this.resetAdvancedSettings();
    }
  }

  /**
   * Show basic settings
   */
  private showBasicSettings() {
    // Hide advanced settings elements
    // Show basic settings elements
  }

  /**
   * Show advanced settings
   */
  private showAdvancedSettings() {
    // Hide basic settings elements
    // Show advanced settings elements
  }

  /**
   * Reset basic settings
   */
  private resetBasicSettings() {
    this.lwActionCategoryBasicSearch = '';
    this.textToSearch = '';
    this.filteredCategoryRules = [];
    this.categoryList = _.cloneDeep(this.categoryListBackup);
  }

  /**
   * Reset advanced settings
   */
  private resetAdvancedSettings() {
    this.lwActionCategoryBasicSearch = '';
    this.textToSearch = '';
    this.filteredCategoryRules = [];
    this.pagination.pageNo = 0;
    this.pagination.pageFrom = 1;
    this.pagination.total = 0;
    this.pagination.disableNext = true;
    this.pagination.disablePrev = true;
    this.pagination.isNextCtrl = true;
    this.pagingButtons = {
      isNext: false,
      isPrevious: false,
      isLast: false,
      isFirst: false
    };
    this.categoryRules = _.cloneDeep(this.categoryRulesBackup);
    this.getPageWiseRuleData();
  }

  /**
   * Search functionality
   */
  onSearchChange() {
    this.searchCategory(this.searchText, this.viewMode === 'basic' ? 'categoryList' : 'categoryRules');
  }

  /**
   * Search categories or rules
   */
  private searchCategory(textToSearch: string, settingType: string) {
    const searchFor = settingType;
    this.textToSearch = '';
    this.pagination.pageNo = 0;
    this.pagination.isNextCtrl = true;

    if (textToSearch && textToSearch.length) {
      textToSearch = textToSearch.toLowerCase();
      this.textToSearch = textToSearch;

      if (settingType === 'categoryList') {
        this.categoryList = this.categoryListBackup.filter((c: Category) => {
          return c.name.toLowerCase().indexOf(textToSearch) >= 0;
        });
      } else {
        this.categoryRules = this.categoryRulesBackup.filter((c: Rule) => {
          return c.name.toLowerCase().indexOf(textToSearch) >= 0;
        });
        this.filteredCategoryRules = _.cloneDeep(this.categoryRules);
        this.getPageWiseRuleData();
      }
    } else {
      if (settingType === 'categoryList') {
        this.categoryList = this.categoryListBackup;
      } else {
        this.getPageWiseRuleData();
      }
    }
  }

  /**
   * Clear search box
   */
  clearSearchBox() {
    this.searchText = '';
    this.onSearchChange();
  }

  /**
   * Close banner
   */
  closeBanner() {
    // Hide banner logic if needed
  }

  /**
   * Edit category
   */
  editCategory(category: Category) {
    const clonedCategory = _.cloneDeep(category);
    this.selectedCategoryDetails = clonedCategory;
    this.modalMode = 'category';
    this.categoryModalDefaultValues.name = clonedCategory.name;
    this.editMode = true;

    // Get saved levels from category
    let savedLevels: string[] = [];
    if (_.hasIn(clonedCategory, 'access_key_rules.0.levels') && !_.isEmpty(clonedCategory.access_key_rules[0].levels)) {
      savedLevels = clonedCategory.access_key_rules[0].levels;
    }

    // Map saved visibility levels to form format
    const organisationLevels: any = {};

    // Update tableRows to set default based on saved values
    const updatedTableRows = this.tableRows.map((row: any) => {
      const isSaved = savedLevels.includes(row.key);
      return {
        ...row,
        visible: isSaved // Set default to true only if this level was saved
      };
    });

    // Initialize all available levels to false, then set saved ones to true
    if (this.tableRows && this.tableRows.length > 0) {
      this.tableRows.forEach((row: any) => {
        organisationLevels[row.key] = savedLevels.includes(row.key);
      });
    }

    // Update default values with mapped organisation levels
    this.categoryModalDefaultValues.levels = updatedTableRows;

    // Update modal title and schema for edit mode
    const modalSchemaCopy = _.cloneDeep(this.categoryModalSchema);
    modalSchemaCopy.title = 'Edit Category';
    // Update tableRows in schema to use the updated defaults
    // modalSchemaCopy.properties.levels.rows = updatedTableRows;
    this.modalSchema = modalSchemaCopy;
    this.defaultValues = this.categoryModalDefaultValues;
    this.isModalOpen = true;
  }

  /**
   * Edit subcategory
   */
  editSubCategory(category: Category) {
    const clonedCategory = _.cloneDeep(category);
    this.selectedSubCategoryDetails = clonedCategory;
    this.modalMode = 'subCategory';
    this.editMode = true;

    // Create a new object with the subcategory name for default values
    const subCategoryDefaultValues = {
      name: clonedCategory.name || '',
      parent_id: clonedCategory.parent_id || '',
      uuid: clonedCategory.uuid || '',
      level: clonedCategory.level || ''
    };

    // Update modal schema for edit mode
    const modalSchemaCopy = _.cloneDeep(this.subCategoryModalSchema);
    modalSchemaCopy.title = 'Edit Sub-Category';
    this.modalSchema = modalSchemaCopy;
    this.defaultValues = subCategoryDefaultValues;
    this.isModalOpen = true;
  }

  /**
   * Delete category
  */
  deleteCategory(category: Category) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    category['brandId'] = this.brandId;
    const level = category.parent_id ? '1' : '0';
    category['level'] = level; // Set level on category object for API call
    const noOfChildren = category.children && category.children.length ? category.children.length : 0;

    this.confirmDeletion(level, noOfChildren, (isDeletion: boolean) => {
      if (isDeletion && this.brandId) {
        this._settingsService.deleteCategory(category)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (res: any) => {
              if (res && res.data && res.data.code == 0) {
                if (level === '0') {
                  this.toastService.showPopup('SUCCESS', 'Success', 'Category deleted successfully.', 3000);
                } else if (level === '1') {
                  this.toastService.showPopup('SUCCESS', 'Success', 'Sub-Category deleted successfully.', 3000);
                }
                this.isModalOpen = false;
                this.afterUpdateBrandConfig();
              } else {
                if (res && res.data && res.data.error_message) {
                  this.toastService.showPopup("ERROR", "Error", res.data.error_message, 3000);
                } else {
                  this.toastService.showPopup("ERROR", "Error", "Error while delete category.", 3000);
                }
              }
            },
            error: (err: any) => {
              console.error('Error while delete category:', err);
              this.toastService.showPopup("ERROR", "ERROR", "Error while delete category.", 3500);
            }
          });
      }
    });
  }

  /**
   * Add subcategory
   */
  addSubcategory(category: Category) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    this.addNewSubCategory(category.uuid);
  }

  /**
   * Add new subcategory
   */
  addNewSubCategory(parentId: string) {
    this.categoryListTableData = this.categoryListTableData.map((data: CategoryTableData) => {
      if (data.parent_id === parentId && data.type === 'html_input') {
        data.isHidden = false;
      }
      return data;
    });
  }

  /**
   * Toggle rule
   */
  toggleRule(rule: Rule) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    rule.is_enabled = !rule.is_enabled;
    this.updateRule(rule, true);
  }


  /**
   * Paginate rules list (feed-list style: first / previous / next / last)
   */
  paginateRulesList(pagingType: string) {
    const totalPages = this.pagination.totalPages || 1;

    if (pagingType === 'first') {
      this.pagination.pageNo = 1;
      this.pagination.isNextCtrl = false;
    } else if (pagingType === 'previous') {
      this.pagination.pageNo = Math.max(1, (this.pagination.pageNo || 1) - 1);
      this.pagination.isNextCtrl = false;
    } else if (pagingType === 'next') {
      this.pagination.isNextCtrl = true;
    } else if (pagingType === 'last') {
      this.pagination.pageNo = totalPages;
      this.pagination.isNextCtrl = false;
    }

    this.getPageWiseRuleData();
  }

  /**
   * Previous page
   */
  previousPage() {
    this.paginateRulesList('previous');
  }

  /**
   * Next page
   */
  nextPage() {
    this.paginateRulesList('next');
  }

  /**
   * Open modal for create/edit
   */
  private openModal(prepopulateData?: any) {
    const editMode = prepopulateData ? 'Update ' : 'New ';
    const data = !_.isEmpty(prepopulateData) ? prepopulateData : {};

    if (this.settings.selected === 'basic') {
      this.modalOptions = this.getBasicModalOption(editMode, data);
    } else {
      this.modalOptions = this.getAdvancedModalOption(editMode, data);
    }

    // Simulate modal opening
  }

  /**
   * Get basic modal option
   */
  private getBasicModalOption(editMode: string, prepopulateData: any) {
    return {
      header: 'Category',
      footerBorderTop: false,
      width: '30%',
      tabs: [{
        header: '',
        id: 'lwCategoryBasicSettingsTab',
        options: [
          {
            name: 'Uuid',
            isRequired: false,
            showLabel: false,
            placeholder: 'Category Name',
            type: 'string',
            sequence: 1,
            classToRender: 'LwInput',
            keyToUpdate: 'uuid',
            display: 'none',
            value: prepopulateData.uuid || ''
          },
          {
            name: 'Name',
            isRequired: true,
            showLabel: true,
            errorMsg: 'Name is mandatory.',
            placeholder: 'Category Name',
            maxLength: 50,
            type: 'string',
            sequence: 1,
            classToRender: 'LwInput',
            keyToUpdate: 'name',
            value: prepopulateData.name || ''
          }
        ]
      }]
    };
  }

  /**
   * Get advanced modal option
   */
  private getAdvancedModalOption(editMode: string, prepopulateData: any) {
    return {
      header: 'Rule',
      tabs: [{
        id: 'lwCategoryAdvancesSettingsRuleTab',
        header: 'Basic',
        is_active: true,
        options: [
          {
            name: 'Id',
            isRequired: false,
            showLabel: false,
            placeholder: 'Unique id of the rule',
            type: 'string',
            sequence: 1,
            classToRender: 'LwInput',
            keyToUpdate: 'id',
            display: 'none',
            value: prepopulateData.id || ''
          },
          {
            name: 'Name',
            isRequired: true,
            showLabel: true,
            placeholder: 'Rule Name',
            type: 'string',
            sequence: 1,
            maxLength: 50,
            classToRender: 'LwInput',
            keyToUpdate: 'name',
            errorMsg: 'Rule Name is mandatory.',
            value: prepopulateData.name || ''
          },
          {
            name: 'Description',
            isRequired: false,
            errorMsg: 'Description is required.',
            showLabel: true,
            placeholder: 'Brief description',
            type: 'string',
            sequence: 4,
            classToRender: 'LwInput',
            isTextArea: true,
            maxLength: 1000,
            cols: 10,
            rows: 5,
            keyToUpdate: 'description',
            value: prepopulateData.description || ''
          }
        ]
      }]
    };
  }

  /**
   * Submit form
   */
  submitForm(data: any) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (this.settings.selected === 'basic') {
      if (this.modalMode === 'subCategory') {
        this.saveSubCategory(data);
      } else {
        this.updateCategory(data);
      }

    } else if (this.settings.selected === 'advanced') {
      const ruleWithSameName = this.categoryRulesBackup.filter((rule: Rule) => {
        return (rule.id !== data.id && rule.name.toLowerCase().trim() === data.name.toLowerCase().trim());
      });

      if (!(ruleWithSameName && ruleWithSameName.length)) {
        if (data.id) {
          this.showConfirmationBox('Confirmation', 'Please note that this change will Override the previous configuration. Are you sure, you want to proceed?', (answer: boolean) => {
            if (answer) {
              this.updateRule(data);
            }
          });
        } else {
          this.updateRule(data);
        }
      } else {
        this.showAlert('Warning', 'Rule name already exists, please enter another name', 'warn');
      }
    }
  }

  saveSubCategory(data: any) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    var categoryDetail = {};
    categoryDetail['parent_id'] = data.parent_id ? data.parent_id : null;
    categoryDetail['level'] = "1";

    var parentId = data.parent_id;
    if (data.uuid) {
      //save existing category with new  name
      categoryDetail['uuid'] = data.uuid ? data.uuid : null;
      categoryDetail['name'] = data.name.trim();
    } else {
      //new category has been added
      categoryDetail['name'] = data.name.trim();
    }
    if (categoryDetail['name']) {
      this.updateCategory(categoryDetail)
    } else {
      Helpers.smallAlertBox("Warning", "Sub-Category name can not be empty", "warn");
    }
  }



  /**
   * Update category/sub-category using SettingsService
  */
  updateCategory(categoryDetail: any) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    categoryDetail['access_key_rules'] = [];
    if (_.get(categoryDetail, 'levels')) {
      var access_key_rules = {};
      access_key_rules['levels'] = [];
      _.each(categoryDetail.levels, (level: any) => {
        if (level.visible) {
          access_key_rules['levels'].push(level.key);
        }
      });
      access_key_rules['type'] = 'visibility';
      categoryDetail['access_key_rules'].push(access_key_rules)
    } else {
      categoryDetail['access_key_rules'].push({ "levels": [], "type": 'visibility' });
    }
    categoryDetail['parent_id'] = _.get(categoryDetail, 'parent_id') ? _.get(categoryDetail, 'parent_id') : null;

    categoryDetail['level'] = _.get(categoryDetail, 'level') ? _.get(categoryDetail, 'level') : "0";
    if (categoryDetail['uuid']) {
      this.showConfirmationBox('Confirmation', 'Please note that this change will Override the previous configuration. Are you sure, you want to proceed?', (answer: boolean) => {
        if (answer) {
          let params: any = {
            brand_id: this.brandDetails._id,
            category_id: categoryDetail['uuid'],
            level: categoryDetail['level'] || "0",
          }
          if (categoryDetail['name']) {
            params['name'] = categoryDetail['name'];
          }
          if (categoryDetail['parent_id']) {
            params['parent_id'] = categoryDetail['parent_id'];
          }
          if (categoryDetail['access_key_rules']) {
            params['access_key_rules'] = categoryDetail['access_key_rules'];
          }
          this._settingsService.updateCategory(this.brandDetails._id, params)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (res: any) => {
                if (res && res.data && res.data.code == 0) {
                  if (categoryDetail['parent_id']) {
                    this.toastService.showPopup('SUCCESS', 'Success', 'Sub-Category updated successfully.', 3000);
                  } else {
                    this.toastService.showPopup('SUCCESS', 'Success', 'Category updated successfully.', 3000);
                  }
                  this.isModalOpen = false;
                  this.afterUpdateBrandConfig();
                } else {
                  if (res && res.data && res.data.error_message) {
                    this.toastService.showPopup("ERROR", "Error", res.data.error_message, 3000);
                  } else {
                    this.toastService.showPopup("ERROR", "Error", "Error while update category.", 3000);
                  }
                }
              },
              error: (err: any) => {
                console.error('Error while update category:', err);
                this.toastService.showPopup("ERROR", "ERROR", "Error while update category.", 3500);
              }
            });
        }
      });
    } else {

      let params: any = {
        brand_id: this.brandDetails._id,
        category_name_list: [categoryDetail['name']],
        level: categoryDetail['level'],
        access_key_rules: categoryDetail['access_key_rules']
      }

      if (categoryDetail['parent_id']) {
        params['parent_id'] = categoryDetail['parent_id'];
      }

      this._settingsService.createCategory(params)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: any) => {
            if (res && res.data && res.data.code == 0) {
              this.resetAndCloseModal();
              this.toastService.showPopup('SUCCESS', 'Success', 'Sub-Category added successfully.', 3000);
            } else {
              this.toastService.showPopup("ERROR", "Error", "Error while create category.", 3000);
            }
          },
          error: (err: any) => {
            console.error('Error while create category:', err);
            this.toastService.showPopup("ERROR", "ERROR", "Error while create category.", 3500);
          }
        });
    }

  }

  /**
   * Update rule using SettingsService
   */
  private updateRule(ruleDetail: Rule, freezeScreen?: boolean) {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (freezeScreen) {
      this.pagerBusy = true;
    }

    if (ruleDetail.id) {
      // If Rule has Id refers existing rule: Perform Update Operation
      this.updateExistingRule(ruleDetail, (isSameAsPreviousRule: boolean, categoriesWithRule: any[]) => {
        if (!isSameAsPreviousRule) {
          this.updateCategories(((ruleDetail.is_deleted) ? 'delete' : 'update'), categoriesWithRule);
        } else {
          this.showAlert('Warning', 'No changes made.', 'warn');
          this.pagerBusy = false;
        }
      });
    } else {
      // New Rule: Perform Add Operation
      ruleDetail.is_enabled = false;
      this.setRuleWithinCategory(ruleDetail, (categoriesWithRule: any[]) => {
        this.updateCategories('add', categoriesWithRule);
      });
    }
  }

  /**
   * Update existing rule
   */
  private updateExistingRule(ruleDetail: Rule, callback: (isSame: boolean, categories: any[]) => void) {
    const oldRuleConfig: any = this.categoryRulesBackup.find((rule: Rule) => rule.id === ruleDetail.id);
    const oldRuleConfigCloned: any = _.cloneDeep(oldRuleConfig);

    if (!ruleDetail.created_date && oldRuleConfig?.created_date) {
      ruleDetail.created_date = oldRuleConfig.created_date;
    }
    if (!ruleDetail.hasOwnProperty('is_enabled') && oldRuleConfig?.hasOwnProperty('is_enabled')) {
      ruleDetail.is_enabled = oldRuleConfig.is_enabled;
    }

    const configToUpdate: any = ruleDetail;
    const categoriesToUpdate: any[] = [];
    let categoryIdsToUnsetRuleFrom: string[] = [];
    let categoryIdsToSetRuleTo: string[] = [];

    const newCategoryList = (configToUpdate.category_list || []).map((category: any) => {
      if (category.uuid) {
        return category.uuid;
      } else {
        return category;
      }
    });
    const oldCategoryList = (oldRuleConfigCloned?.category_list || []).map((category: any) => {
      if (category.uuid) {
        return category.uuid;
      } else {
        return category;
      }
    });

    if (ruleDetail && ruleDetail.is_deleted) {
      categoryIdsToUnsetRuleFrom = oldCategoryList || [];
    } else {
      categoryIdsToUnsetRuleFrom = _.difference(oldCategoryList, newCategoryList);
      categoryIdsToSetRuleTo = _.difference(newCategoryList, categoryIdsToUnsetRuleFrom);
    }

    // Remove rules from the categories 
    _.each(categoryIdsToUnsetRuleFrom, (categoryId) => {
      // Get the categories detail
      let categoryDetail: any = _.find(this.categoryListBackup, { "uuid": categoryId });
      if (categoryDetail && categoryDetail["rules"]) {
        // Remove the rule from the categories
        categoryDetail["rules"] = _.filter(categoryDetail["rules"], (rule: any) => {
          // Ignore where rule doesn't match with the new rule
          return rule.id !== ruleDetail.id;
        });
      }
      categoriesToUpdate.push(categoryDetail);
    });

    // Update or add rules to the categories
    _.each(categoryIdsToSetRuleTo, (categoryId) => {
      // Get the category detail
      let categoryDetail: any = _.find(this.categoryListBackup, { "uuid": categoryId });
      if (!(categoryDetail["rules"] && categoryDetail["rules"].length)) {
        // If no rules appiled to category then set them blank array
        categoryDetail["rules"] = [];
        categoryDetail["rules"].push(ruleDetail);
      }
      categoryDetail["rules"] = _.filter(categoryDetail["rules"], function (rule) {
        // If rule match then update
        var newRule = (rule.id == ruleDetail.id) ? Object.assign(rule, ruleDetail) : rule;
        delete newRule["category_list"];
        return newRule;
      });
      categoriesToUpdate.push(categoryDetail);
    })
    callback(false, categoriesToUpdate)
  }

  /**
   * Set rule within category
   */
  private setRuleWithinCategory(rule: Rule, callback: (categories: any[]) => void) {
    const categoriesToUpdate: any[] = [];
    rule.created_date = new Date();
    rule.id = this.generateId('lw_category_rule');

    const ruleToAdd = _.cloneDeep(rule);
    delete ruleToAdd.category_list;

    _.each(rule.category_list, (category: any) => {
      const categoryDetail = this.categoryListBackup.find((cat: Category) => cat.uuid === category.uuid);
      if (categoryDetail) {
        if (!categoryDetail.rules) {
          categoryDetail.rules = [];
        }
        categoryDetail.rules.push(ruleToAdd);
        categoriesToUpdate.push(categoryDetail);
      }
    });
    callback(categoriesToUpdate);
  }

  /**
   * Update categories using SettingsService
   */
  private updateCategories(type: string, categoriesWithRule: any[]) {
    this._settingsService.updateCategoryRuleBasedOnType(2, this.brandId, categoriesWithRule)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            if (response.data.code === 0) {
              this.resetAndCloseModal(); // Reset and close modal
              if (type === 'add') {
                this.showAlert('Success', 'Rule saved successfully, please activate from the list.', 'success');
              } else {
                this.showAlert('Success', 'Rule ' + type + 'ed successfully.', 'success');
              }
            } else if (response.data.code === 4022) {
              this.showAlert('Warning', 'No changes made.', 'warn');
            } else {
              this.showAlert('Error', 'Problem while ' + type + 'ing Rule.', 'error');
            }
          } else {
            this.showAlert('Error', 'Problem while ' + type + 'ing Rule.', 'error');
          }
        },
        error: (error: any) => {
          console.error('Error while updating categories:', error);
          this.showAlert('Error', 'Problem while updating categories rules.', 'error');
        }
      });
  }

  /**
   * Generate ID
   */
  private generateId(idPrefix: string): string {
    return (idPrefix.toString() + '_' + (new Date()).getTime()).toString() + this.getRandomString();
  }

  /**
   * Get random string
   */
  private getRandomString(): string {
    return Math.random().toString(36).substring(2, 15);
  }

  /**
   * Reset and close modal
   */
  private resetAndCloseModal() {
    this.pagination.pageNo = 0;
    this.getCategoryList();
    this.closeModal();
  }

  /**
   * Close modal
   */
  private closeModal() {
    this.closeCategoryModal();
  }

  /**
   * Confirm deletion
   */
  private confirmDeletion(level: string, noOfChildren: number, callback: (isDeletion: boolean) => void) {
    let message = '';
    if (level === '0' && noOfChildren) {
      message = 'Deleting Category will cause deletion of all related Sub-Categories. Are you sure, you want to delete Category?';
    } else if (level === '0' && !noOfChildren) {
      message = 'Are you sure, you want to delete Category?';
    } else if (level === '1') {
      message = 'Are you sure, you want to delete Sub-Category?';
    }

    this.showConfirmationBox('Delete Category', message, callback);
  }

  /**
   * Show confirmation box
   */
  private showConfirmationBox(title: string, message: string, callback: (answer: boolean) => void) {
    if (Helpers && Helpers.SmartMessageBox) {
      Helpers.SmartMessageBox({
        title: title,
        content: message,
        buttons: [
          { text: "No", type: "secondary", key: "No" },
          { text: "Yes", type: "primary", key: "Yes" }
        ]
      }, (ButtonPressed: string) => {
        if (ButtonPressed === 'Yes') {
          callback(true)
        } else {
          callback(false)
        }
      });
    } else {
      // Fallback
      const answer = confirm(message);
      callback(answer);
    }
  }

  /**
   * Show alert
   */
  private showAlert(title: string, message: string, type: string) {
    if (Helpers && Helpers.smallAlertBox) {
      Helpers.smallAlertBox(title, message, type);
    } else {
    }
  }

  /**
   * Save the categories configuration using SettingsService
   */
  saveConfiguration(params) {

    this._settingsService.createCategory(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          if (res && res.data && res.data.code == 0) {
            this.toastService.showPopup("SUCCESS", "Success", "Category added successfully.", 3000);
            this.isModalOpen = false;
            this.afterUpdateBrandConfig();
          } else {
            if (res && res.data && res.data.error_message) {
              this.toastService.showPopup("ERROR", "Error", res.data.error_message, 3000);
            } else {
              this.toastService.showPopup("ERROR", "Error", "Error while save category.", 3000);
            }
          }
          this.isSaving = false;
        },
        error: (err: any) => {
          console.error('Error saving general configurations:', err);
          this.toastService.showPopup("ERROR", "ERROR", "Error while save category.", 3500);
          this.isSaving = false;
        }
      });
  }

  /**
 * get the latest brand details and update it on helpers and app state
 */
  afterUpdateBrandConfig() {
    this.accountsService.getBrandDetail(this.brandDetails._id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response) => {
      if (response.code === 0 && response.data.brand) {
        this.brandDetails = response.data.brand;
        this.stateService.updateBrandDetails(this.brandDetails);
      }
    });
  }

  /**
   * Cancel the categories configuration changes
   */
  cancelConfiguration() {
    if (this.isCancelling) return;

    this.isCancelling = true;

    // Reset data to original state by reloading from server
    this.onChange();

    setTimeout(() => {
      this.showAlert('Info', 'Categories settings have been reset', 'info');
      this.isSaveEnabled = false;
      this.isCancelling = false;
    }, 500);
  }

  /**
   * Can deactivate guard implementation
   */
  canDeactivate(): Promise<boolean> | boolean {
    const hasUnsavedChanges = this.isSaveEnabled;
    if (hasUnsavedChanges) {
      return this.confirmDiscardChanges();
    }
    return true;
  }

  /**
   * Show confirmation dialog for unsaved changes
   */
  confirmDiscardChanges(): Promise<boolean> {
    return new Promise((resolve) => {
      if (Helpers && Helpers.SmartMessageBox) {
        Helpers.SmartMessageBox(
          {
            title: "You have reordered the rows",
            content: "To save your changes, please use the <b>Save</b> button at the top right of the page. Click <b>Discard Changes</b> to lose your edits and continue. Click <b>Cancel</b> to close this popup and keep editing.",
            buttons: [
              { text: "Cancel", type: "secondary", key: "Cancel" },
              { text: "Discard Changes", type: "primary", key: "Discard" }
            ]
          },
          (ButtonPressed: string) => {
            setTimeout(() => {
              if (ButtonPressed === "Discard") {
                this.isSaveEnabled = false;
                resolve(true);
              } else {
                resolve(false);
              }
            }, 50);
          }
        );
      } else {
        // Fallback if Helpers is not available
        const shouldDiscard = confirm('You have unsaved changes. Are you sure you want to discard them?');
        if (shouldDiscard) {
          this.isSaveEnabled = false;
        }
        resolve(shouldDiscard);
      }
    });
  }
}